<template>
  <v-card>
    <v-card-title class="pb-1">
      Nutzung
      <span class="caption text--secondary ml-2">{{ windowLabel }}</span>
      <v-spacer></v-spacer>
      <v-btn icon :loading="loading" @click="load()">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-card-title>

    <v-alert v-if="error" type="warning" dense text class="mx-4 mb-2">
      {{ error }}
    </v-alert>

    <template v-if="stats">
      <!-- Kennzahlen. "extern" steht vorn und mit Anteil, weil das die Frage
           beantwortet, um die es hier geht: wie viel Fremdnutzung liegt vor.
           Der eigene Heimnetzverkehr stellt regelmäßig ein Drittel bis die
           Hälfte aller Requests und würde die Zahl sonst aufblähen. -->
      <v-card-text class="pt-0 pb-1">
        <div class="d-flex flex-wrap kpis">
          <div class="kpi">
            <div class="kpi-value">{{ formatNumber(totals.extern_requests) }}</div>
            <div class="kpi-label">Requests extern ({{ externShare }})</div>
          </div>
          <div class="kpi">
            <div class="kpi-value">{{ formatNumber(totals.extern_ips) }}</div>
            <div class="kpi-label">fremde IP-Adressen</div>
          </div>
          <div class="kpi">
            <div class="kpi-value">{{ formatNumber(totals.local_requests) }}</div>
            <div class="kpi-label">Requests Heimnetz</div>
          </div>
          <div class="kpi">
            <div class="kpi-value">{{ errorRate }}</div>
            <div class="kpi-label">Fehlerquote (4xx/5xx)</div>
          </div>
          <div class="kpi">
            <div class="kpi-value">{{ traffic }}</div>
            <div class="kpi-label">Traffic</div>
          </div>
          <div class="kpi">
            <div class="kpi-value">{{ avgRequestTime }}</div>
            <div class="kpi-label">⌀ Antwortzeit</div>
          </div>
        </div>
      </v-card-text>

      <v-card-text class="pt-2 pb-0">
        <div class="d-flex align-center mb-1">
          <span class="subtitle-2">Requests pro Tag</span>
          <v-spacer></v-spacer>
          <v-btn x-small text @click="showTable = !showTable">
            {{ showTable ? 'Tabelle ausblenden' : 'als Tabelle' }}
          </v-btn>
        </div>

        <!-- Balken als DOM-Elemente statt SVG: der Container ist ~1570 px breit,
             ein mitskalierendes viewBox-SVG wurde dadurch 314 px hoch und die
             Schrift darin 1,6-fach vergrößert. So bleibt die Höhe fest und die
             Typografie die des restlichen Panels. -->
        <div class="chart" role="img" aria-label="Requests pro Tag im gewählten Zeitfenster">
          <div class="chart-plot">
            <div
                v-for="bar in bars"
                :key="bar.day"
                class="slot"
                @mouseenter="hovered = bar"
                @mouseleave="hovered = null"
            >
              <!-- Label nur am Maximum, nicht an jedem Balken. -->
              <span v-if="peak && peak.day === bar.day" class="peak-label">
                {{ formatNumber(bar.requests) }}
              </span>
              <div
                  class="bar"
                  :style="{ height: bar.height }"
                  :title="`${bar.label}: ${formatNumber(bar.requests)} Requests`"
              ></div>
            </div>

            <div v-if="hovered" class="tooltip" :style="tooltipStyle">
              <strong>{{ hovered.label }}</strong><br>
              {{ formatNumber(hovered.requests) }} Requests<br>
              {{ formatNumber(hovered.uniqueIps) }} IPs
            </div>
          </div>

          <div class="chart-axis">
            <span>{{ firstLabel }}</span>
            <span>{{ lastLabel }}</span>
          </div>
        </div>

        <v-simple-table v-if="showTable" dense class="mt-2">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Tag</th>
                <th class="text-right">Requests</th>
                <th class="text-right">IPs</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="day in stats.daily" :key="day.day">
                <td class="caption">{{ formatDay(day.day) }}</td>
                <td class="text-right caption">{{ formatNumber(day.requests) }}</td>
                <td class="text-right caption">{{ formatNumber(day.unique_ips) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>

      <v-card-text class="pt-4 pb-0">
        <div class="subtitle-2 mb-1">Nach Dienst</div>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Host</th>
                <th class="text-right">Requests</th>
                <th class="text-right">Anteil</th>
                <th class="text-right">IPs</th>
                <th class="text-right">Fehler</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in hosts.hosts" :key="entry.host">
                <td class="caption">{{ entry.host }}</td>
                <td class="text-right caption">{{ formatNumber(entry.requests) }}</td>
                <td class="text-right caption">{{ hostShare(entry) }}</td>
                <td class="text-right caption">{{ formatNumber(entry.unique_ips) }}</td>
                <td class="text-right caption">{{ formatNumber(entry.errors) }}</td>
              </tr>
              <tr v-if="!hosts.hosts || !hosts.hosts.length">
                <td colspan="5" class="caption text--secondary">
                  Im Zeitfenster ist noch kein Request mit Host protokolliert.
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <!-- Der Hinweis gehört zwingend an die Tabelle: `host` wird erst seit
             der nginx-Umstellung mitgeschrieben, im Übergangszeitraum ist die
             Summe der Dienste ein Bruchteil des Fensters. -->
        <div class="caption text--secondary mt-1">
          <template v-if="hosts.known_since">
            Dienste werden seit {{ formatTs(hosts.known_since) }} mitgeschrieben —
            {{ formatNumber(hosts.unknown_requests) }} Requests im Fenster
            ({{ unknownShare }}) stammen aus der Zeit davor und sind keinem Dienst
            zugeordnet.
          </template>
          <template v-else>
            <code>web_requests.host</code> ist noch nirgends gefüllt — sobald nginx
            <code>$host</code> mitschreibt, erscheinen die Dienste hier.
          </template>
        </div>
      </v-card-text>

      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="4">
            <div class="subtitle-2 mb-1">Häufigste Adressen</div>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr
                      v-for="entry in stats.top_ips"
                      :key="entry.ip"
                      class="clickable"
                      @click="$emit('select', entry.ip)"
                  >
                    <td class="caption">
                      {{ entry.ip }}
                      <span class="text--secondary">{{ ipPlace(entry) }}</span>
                    </td>
                    <td class="text-right caption" style="white-space: nowrap">
                      {{ formatNumber(entry.requests) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>

          <v-col cols="12" md="4">
            <div class="subtitle-2 mb-1">Häufigste Pfade</div>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr v-for="entry in stats.top_uris" :key="entry.uri || 'leer'">
                    <td class="caption text-truncate" style="max-width: 220px">
                      {{ entry.uri || 'ohne Pfad' }}
                    </td>
                    <td class="text-right caption" style="white-space: nowrap">
                      {{ formatNumber(entry.requests) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>

          <v-col cols="12" md="4">
            <div class="subtitle-2 mb-1">Häufigste User-Agents</div>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr
                      v-for="entry in stats.top_user_agents"
                      :key="entry.user_agent || 'unbekannt'"
                  >
                    <td class="caption text-truncate" style="max-width: 220px">
                      {{ entry.user_agent || 'unbekannt' }}
                    </td>
                    <td class="text-right caption" style="white-space: nowrap">
                      {{ formatNumber(entry.requests) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>

      </v-card-text>
    </template>

    <v-card-text v-else-if="loading" class="text--secondary">
      Auswertung läuft — über 30 Tage dauert der erste Aufbau rund 20 Sekunden.
    </v-card-text>
  </v-card>
</template>

<script>
import { weblogApi } from '../../services/weblog'

// Die Statistik wird im Backend 10 Minuten gecacht — häufiger zu pollen holt
// dieselbe Antwort. Die Karte daneben pollt alle 30 s, das genügt hier nicht.
const REFRESH_MS = 300000

const WINDOW_LABELS = {
  1: 'letzte Stunde',
  24: 'letzte 24 Stunden',
  168: 'letzte 7 Tage',
  720: 'letzte 30 Tage',
}

// Kleinste sichtbare Balkenhöhe in Prozent. Ein Tag mit wenigen Requests neben
// einem Tag mit 100k wäre sonst rechnerisch 0 % hoch und damit nicht von einem
// Tag ohne jeden Verkehr zu unterscheiden.
const MIN_BAR_PERCENT = 1.5

export default {
  name: 'UsageStats',
  props: {
    hours: { type: Number, default: 24 },
  },
  data: () => ({
    stats: null,
    loading: false,
    error: null,
    hovered: null,
    showTable: false,
    requestSeq: 0,
    timer: null,
  }),
  computed: {
    windowLabel() {
      return WINDOW_LABELS[this.hours] || `${this.hours} h`
    },
    totals() {
      return (this.stats && this.stats.totals) || {}
    },
    hosts() {
      return (this.stats && this.stats.hosts) || {}
    },
    unknownShare() {
      const total = this.totals.total_requests
      if (!total) return '0 %'
      return `${Math.round((this.hosts.unknown_requests / total) * 100)} %`
    },
    externShare() {
      const total = this.totals.total_requests
      if (!total) return '0 %'
      return `${Math.round((this.totals.extern_requests / total) * 100)} % von allen`
    },
    errorRate() {
      const status = (this.stats && this.stats.status) || {}
      return `${this.formatDecimal((status.error_rate || 0) * 100)} %`
    },
    traffic() {
      const bytes = ((this.stats && this.stats.traffic) || {}).bytes_total || 0
      if (bytes >= 1e12) return `${this.formatDecimal(bytes / 1e12)} TB`
      if (bytes >= 1e9) return `${this.formatDecimal(bytes / 1e9)} GB`
      if (bytes >= 1e6) return `${this.formatDecimal(bytes / 1e6)} MB`
      return `${this.formatNumber(bytes)} B`
    },
    avgRequestTime() {
      const value = ((this.stats && this.stats.traffic) || {}).avg_request_time
      // null heißt "nichts gemessen" — als "0 ms" gelesen wäre das eine Aussage
      // über die Geschwindigkeit, die die Daten nicht hergeben.
      if (value === null || value === undefined) return '–'
      return value < 1
          ? `${this.formatNumber(Math.round(value * 1000))} ms`
          : `${this.formatDecimal(value, 2)} s`
    },
    bars() {
      const days = (this.stats && this.stats.daily) || []
      if (!days.length) return []
      const max = Math.max(...days.map((d) => d.requests), 1)
      return days.map((day, index) => {
        const share = (day.requests / max) * 100
        return {
          day: day.day,
          label: this.formatDay(day.day),
          requests: day.requests,
          uniqueIps: day.unique_ips,
          index,
          // Nulltage bleiben bei 0 % — sie sollen als Lücke sichtbar sein.
          height: day.requests ? `${Math.max(share, MIN_BAR_PERCENT)}%` : '0%',
        }
      })
    },
    peak() {
      return this.bars.reduce(
          (best, bar) => (best && best.requests >= bar.requests ? best : bar), null)
    },
    firstLabel() {
      return this.bars.length ? this.bars[0].label : ''
    },
    lastLabel() {
      return this.bars.length ? this.bars[this.bars.length - 1].label : ''
    },
    tooltipStyle() {
      if (!this.hovered) return {}
      // Prozentual statt in Pixeln, damit die Blase unabhängig von der
      // Containerbreite über ihrem Balken sitzt.
      const slots = this.bars.length || 1
      const center = ((this.hovered.index + 0.5) / slots) * 100
      // An den Rändern nicht aus der Karte laufen lassen.
      return { left: `${Math.min(Math.max(center, 6), 94)}%` }
    },
  },
  watch: {
    hours() { this.load() },
  },
  mounted() {
    this.load()
    this.timer = window.setInterval(() => this.load({ silent: true }), REFRESH_MS)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    clearInterval(this.timer)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    onVisibilityChange() {
      if (!document.hidden) this.load({ silent: true })
    },
    formatNumber(value) {
      return (value || 0).toLocaleString('de-DE')
    },
    // toFixed() liefert einen Punkt als Dezimaltrenner — in einer deutschen UI
    // gehört dort ein Komma hin.
    formatDecimal(value, digits = 1) {
      return (value || 0).toLocaleString('de-DE', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    },
    formatTs(value) {
      if (!value) return '–'
      return new Date(value).toLocaleString('de-DE')
    },
    // Anteil an den *zugeordneten* Requests, nicht am Fenster: solange die
    // Mehrheit der Zeilen aus der Zeit vor der nginx-Umstellung stammt, wären
    // Prozente vom Fenster für jeden Dienst 0 % und damit nutzlos.
    hostShare(entry) {
      const attributed = this.hosts.attributed_requests
      if (!attributed) return '–'
      return `${this.formatDecimal((entry.requests / attributed) * 100)} %`
    },
    formatDay(value) {
      if (!value) return '–'
      const [year, month, day] = value.split('-')
      return `${day}.${month}.${year}`
    },
    ipPlace(entry) {
      const parts = [entry.city, entry.country_code].filter(Boolean)
      return parts.length ? ` · ${parts.join(', ')}` : ''
    },
    async load({ silent = false } = {}) {
      // Gleiches Sequenznummer-Muster wie Karte und Detailpanel: eine überholte
      // Antwort darf keine neuere überschreiben.
      const seq = ++this.requestSeq
      if (!silent) this.loading = true
      this.error = null
      try {
        const data = await weblogApi.stats(this.hours)
        if (seq !== this.requestSeq) return
        this.stats = data
      } catch (e) {
        if (seq !== this.requestSeq) return
        console.error('[UsageStats]', e)
        // Zahlen absichtlich stehen lassen: eine leergeräumte Auswertung sieht
        // aus wie "keine Nutzung" und wäre irreführender als veraltete Werte.
        this.error = 'Auswertung konnte nicht geladen werden'
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* #app setzt global text-align: center — ohne Umstellung rendern Kennzahlen und
   Tabellen zentriert. Gleiche Präzedenz wie RequestMap.vue und IpDetailPanel. */
.v-card {
  text-align: left;
}

.kpis {
  gap: 24px 32px;
}

.kpi-value {
  font-size: 1.35rem;
  line-height: 1.2;
  font-weight: 500;
}

.kpi-label {
  font-size: 0.75rem;
  opacity: 0.7;
}

.chart-plot {
  position: relative;
  display: flex;
  align-items: flex-end;
  /* 2 px Abstand zwischen den Balken: Flächen derselben Farbe brauchen eine
     Trennung durch die Hintergrundfarbe, sonst verschmelzen benachbarte Tage. */
  gap: 2px;
  height: 180px;
  /* Zurückgenommenes Raster: nur die Nulllinie, sichtbar genug zum Einordnen. */
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

/* Trefferfläche über die volle Höhe: ein Tag mit wenig oder ohne Verkehr wäre
   sonst kaum oder gar nicht zu treffen. */
.slot {
  position: relative;
  flex: 1 1 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-width: 2px;
}

/* Eine Serie, eine Farbe. Teal statt der Kartenfarben, damit die Balken nicht
   mit deren Bedeutung (blau = lokal, rot = fehlerlastig, grün = normal)
   verwechselt werden. Gegen die dunkle Card-Fläche auf Helligkeitsband, Chroma
   und Kontrast (>= 3:1) geprüft. Gerundet nur oben — der Fuß gehört auf die
   Nulllinie. */
.bar {
  width: 100%;
  background: #26a69a;
  border-radius: 4px 4px 0 0;
}

.slot:hover .bar {
  background: #4db6ac;
}

.peak-label {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 0.7rem;
  opacity: 0.6;
}

.chart-axis {
  display: flex;
  justify-content: space-between;
  font-size: 0.7rem;
  opacity: 0.6;
  margin-top: 4px;
}

.tooltip {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.85);
  font-size: 0.75rem;
  line-height: 1.3;
  white-space: nowrap;
  pointer-events: none;
}

.clickable {
  cursor: pointer;
}
</style>
